import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';

/**
 * Shared by every Flex*Card in components/Listing/ - was previously
 * reimplemented (with small variations) in FlexPeopleCard, FlexCourseCard,
 * FlexNewsCard, FlexPublicationCard, and FlexFullwidthCard.
 */

/**
 * Get the raw image field data for `fieldName` from a listing/search-result
 * item, wherever the API happened to put it (a full object field vs. the
 * catalog `image_scales` metadata column used by search/listing results).
 */
export function getCardImageField(item, fieldName) {
  if (item[fieldName]) return item[fieldName];
  if (item.image_scales?.[fieldName]?.[0]) {
    return item.image_scales[fieldName][0];
  }
  return null;
}

/**
 * Resolve a download URL from an image field entry (as returned by
 * getCardImageField), trying each scale name in `scalePreference` in order,
 * falling back to the raw (unscaled) download if none of them match.
 * Scale preference intentionally stays a per-card argument, not a single
 * shared order - a People headshot and a News hero image want different
 * scales tried first, and forcing one global order would visibly change
 * which image size some cards pick.
 *
 * Both halves of the URL go through flattenToAppURL. Only the `download`
 * half used to, which was invisible in listings - search results already
 * carry a public `@id` - but not in a full view: there `content.items[]`
 * is serialized during SSR with the internal backend URL, so the photo
 * grid on a Competition/Robot page emitted
 * `http://backend:8080/Plone/...` into the public HTML, both as the <img>
 * source and as the link to the full-size image.
 */
export function resolveScaledImageUrl(item, imageSource, scalePreference) {
  if (!imageSource) return null;
  const base = flattenToAppURL(item['@id']);
  for (const scale of scalePreference) {
    const download = imageSource.scales?.[scale]?.download;
    if (download) return base + '/' + flattenToAppURL(download);
  }
  if (imageSource.download) {
    return base + '/' + flattenToAppURL(imageSource.download);
  }
  return null;
}

/**
 * onError handler for a card image: swap to `fallbackSrc` once, and if even
 * the fallback fails to load, hide the image (and its surrounding
 * `.cards__image-container`, if any) instead of showing a broken-image icon.
 */
export function createImageErrorHandler(fallbackSrc) {
  return (e) => {
    const urlObj = new URL(e.target.src, window.location.origin);
    const fallbackPath = fallbackSrc.startsWith('/')
      ? fallbackSrc
      : '/' + fallbackSrc;

    if (urlObj.pathname !== fallbackPath) {
      e.target.src = fallbackSrc;
    } else {
      const container = e.target.closest('.cards__image-container');
      if (container) container.style.display = 'none';
      e.target.style.display = 'none';
    }
  };
}

/**
 * How a card should fit its image into the fixed 16:9 box, as
 * `{objectFit, objectPosition}` - or null to leave the CSS alone.
 *
 * Two decisions, both made by the backend when the image is saved (see
 * academic.types' focal_point subscriber) and both correctable on the item:
 *
 * `image_fit` says whether the picture may be cropped at all. A photograph
 * may; a logo, a banner or a multi-panel figure loses part of itself, so
 * those are shown whole inside the box instead. 216 of the site's 674 lead
 * images are in that second group, most of them publication teaser figures.
 *
 * `image_focal_point` says where a crop should sit. `object-fit: cover` with
 * the browser's default takes the middle of the source, which on a portrait
 * photo is chest height - so heads got cut off, which is not acceptable for
 * the PhD-defence pictures under /news. Measured against the faces in the
 * 117 cropped photos that have any, the detected value keeps them all in
 * frame on 98 - every photo whose faces fit a 16:9 window at all - where
 * plain centring manages 42 and a value derived from the image dimensions
 * alone manages 68. Without a value, cards centre as they always did.
 */
export function cardImageFit(item) {
  const fit = item.image_fit?.trim();
  if (fit === 'contain') {
    // Nothing is cropped away, so a focal point would have nothing to move.
    return { objectFit: 'contain' };
  }
  const focalPoint = normalizeFocalPoint(item.image_focal_point);
  return focalPoint ? { objectPosition: `50% ${focalPoint}` } : null;
}

/**
 * Accept what a person types, not only what the detector writes.
 *
 * The field is a plain text line. The subscriber always writes "18%", but
 * someone correcting a crop by hand reasonably types "18" - and
 * `object-position: 50% 18` is a number without a unit, which is invalid, so
 * the browser drops the whole declaration and the correction silently does
 * nothing. A bare number is read as a percentage; anything already carrying a
 * unit is passed through untouched.
 */
function normalizeFocalPoint(value) {
  const text = value?.trim();
  if (!text) return null;
  return /^[+-]?(\d+\.?\d*|\.\d+)$/.test(text) ? `${text}%` : text;
}
